import { useAppStore } from '../../../shared/hooks/useAppStore';
import React from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { ORDER_ROUTES } from '../../order/order.constants';
import { PAYMENT_ROUTES } from '../payment.constants';
import { collectBrowserFingerprint } from '../payment.browser';
import { resolvePaymentGateway, resolveWalletSheet } from '../payment.gateway';
import { postApplePayment } from '../api/payment.api';
import { useApplePaySession } from '../hooks/useApplePaySession';
import { usePostEmerchantPayment } from '../hooks/usePostEmerchantPayment';
import { usePostGooglePayment } from '../hooks/usePostGooglePayment';
import { usePostMyPosPayment } from '../hooks/usePostMyPosPayment';
import { ApplePayButton } from './ApplePayButton/ApplePayButton';
import { EMerchantPay } from './EMerchantPay';
import { GooglePayBtn } from './GooglePayBtn';
import { MiaPayButton } from './MiaPayButton/MiaPayButton';
import { MyPosPayButton } from './MyPosPayButton';
import { TBCPayButton } from './TBCPayButton/TBCPayButton';

import { SUBUNITS_IN_CURRENCY } from '../../../shared/constants';
import { PORTMONE_MERCHANT_ID } from '../../../shared/env.config';
import {
    GOOGLE_PAY_GATEWAY,
    MERCHANT_NAME,
    MYPOS_CURRENCY_CODE,
    PAYMENT_NAVIGATION_DELAY,
    THREE_DS_REDIRECT_DELAY,
    TRANZZO_PAY_GATEWAY,
} from '../payment.constants';
import { debug } from '../../../shared/lib/general';

import { EVENT_NAMES, NoUriProvider, RedirectProvider, useSendEventLog, WaitSource } from '../../event-log';
import { useLoyaltyStore } from '../../../modules/loyalty';
import { GoogleCommitResult, PaymentError, RedirectPaymentResult, WaitNotice } from '../payment.types';
import style from './PaymentButtons.module.css';

const PaymentButtons = () => {
    const order = useAppStore((s) => s.order);
    const vm = useAppStore((s) => s.vm);
    const navigate = useNavigate();

    const gateway = resolvePaymentGateway(vm?.payment_provider_id);
    const walletSheet = resolveWalletSheet();
    const isSandbox = Boolean(vm?.is_sandbox);

    const emerchantPayment = usePostEmerchantPayment();
    const googlePayment = usePostGooglePayment();
    const myPosPayment = usePostMyPosPayment();
    const { sendEvent } = useSendEventLog();

    const getLatestLoyaltyId = () => useLoyaltyStore.getState().loyalty_id;

    const getFailedPath = () =>
        generatePath(ORDER_ROUTES.failed, { id: String(order.id) });
    const getWaitPath = () =>
        generatePath(PAYMENT_ROUTES.wait, { id: String(order.id) });

    const onError = (err: PaymentError) => {
        debug(err.title, 'error', err.error);
        sendEvent({ name: EVENT_NAMES.static.PAYMENT_FAILED_ERROR });
        navigate(getFailedPath(), { replace: true });
    };

    const createRedirectHandler = (providerName: NoUriProvider) => (result: RedirectPaymentResult) => {
        if (result.uri) {
            window.location.href = result.uri;
        } else {
            sendEvent({ name: EVENT_NAMES.paymentFailedNoUri[providerName] });
            navigate(getFailedPath(), { replace: true });
        }
    };

    const createFailedHandler = (providerName: RedirectProvider) => () => {
        sendEvent({ name: EVENT_NAMES.paymentFailed[providerName] });
        navigate(getFailedPath(), { replace: true });
    };

    const navigateToWait = (waitSource: WaitSource): ReturnType<typeof setTimeout> =>
        setTimeout(() => {
            navigate(getWaitPath(), { replace: true, state: { waitSource } });
        }, PAYMENT_NAVIGATION_DELAY);

    /**
     * The commit went to 3DS: nothing is charged until the buyer confirms with
     * the bank, which returns them to `?oid=` (payment-service `result_url`).
     * The notice replaces the bare «Wait» so the jump to a bank page is expected.
     */
    const redirectToThreeDs = (threeDsUrl: string) => {
        sendEvent({ name: EVENT_NAMES.static.PAYMENT_REDIRECTED_3DS_GOOGLE });
        navigate(getWaitPath(), {
            replace: true,
            state: { waitSource: 'google' satisfies WaitSource, notice: 'three-ds' satisfies WaitNotice },
        });
        setTimeout(() => {
            window.location.href = threeDsUrl;
        }, THREE_DS_REDIRECT_DELAY);
    };

    const onGoogleCommitted = (result: GoogleCommitResult, waitTimer: ReturnType<typeof setTimeout>) => {
        if (!result.three_ds_url) return;
        // A commit faster than PAYMENT_NAVIGATION_DELAY would otherwise land the
        // plain «Wait» navigation after this one and wipe the 3DS notice.
        clearTimeout(waitTimer);
        redirectToThreeDs(result.three_ds_url);
    };

    const onEmerchantPaySuccess = (provider: string, paymentResponse: Record<string, unknown> | google.payments.api.PaymentData) => {
        navigateToWait('emerchant');
        emerchantPayment.mutate(
            {
                orderId: order?.id as string,
                loyaltyId: getLatestLoyaltyId(),
                paymentRequest: { provider, ...paymentResponse },
            },
            {
                onError: (error) => onError({ title: 'EMerchantPay payment error', error }),
            },
        );
    };

    const onGoogleCheckoutSuccess = (paymentResponse: google.payments.api.PaymentData) => {
        const waitTimer = navigateToWait('google');
        // The success path goes through the promise, not `onSuccess` in the
        // options: the navigation to «Wait» unmounts these buttons within 100 ms,
        // the commit takes seconds, and React Query drops per-call callbacks once
        // the observer is unmounted. `onError` stays as before: after «Wait» the
        // screen's polling owns the failure, for Portmone as well as Tranzzo.
        googlePayment
            .mutateAsync(
                {
                    orderId: order?.id as string,
                    loyaltyId: getLatestLoyaltyId(),
                    // Sent for every gateway: the server forwards it only to
                    // Tranzzo and keeps the PHP gateway's `info` as it was.
                    paymentRequest: { ...paymentResponse, browser: collectBrowserFingerprint() },
                },
                {
                    onError: (error) => onError({ title: 'Google payment error', error }),
                },
            )
            .then(
                (result) => onGoogleCommitted(result, waitTimer),
                () => undefined,
            );
    };

    const onMyPosCheckoutSuccess = (_paymentResponse: Record<string, unknown>) => {
        sendEvent({ name: EVENT_NAMES.paymentInitiated.MyPos });
        navigateToWait('mypos');
        myPosPayment.mutate(
            {
                orderId: order?.id as string,
                loyaltyId: getLatestLoyaltyId(),
            },
            {
                onError: (error) => onError({ title: 'MyPos payment error', error }),
            },
        );
    };

    const { requestApplePay } = useApplePaySession({
        currencyCode: vm?.currency_code,
        countryCode: 'UA',
        onPaymentAuthorized: (applePayment, completePayment) => {
            completePayment();
            postApplePayment(
                order?.id ?? null,
                getLatestLoyaltyId(),
                applePayment.payment.token,
            ).catch((e) => {
                sendEvent({ name: EVENT_NAMES.static.PAYMENT_FAILED_APPLE_SERVER_REQUEST });
                onError({ title: 'Apple payment error', error: e });
            });
            navigate(getWaitPath(), { replace: true, state: { waitSource: 'apple' satisfies WaitSource } });
        },
    });

    const orderAmount = (order?.amount || 0) / SUBUNITS_IN_CURRENCY;

    /**
     * The single branching point. Each gateway renders a different component with
     * different props, so this is the factory Rule 3 asks for rather than a lookup
     * table. The `never` default makes adding a gateway to the union a compile
     * error until it is handled here.
     *
     * Exactly one button renders: a machine has one gateway, and the default one
     * delegates to whichever wallet sheet the device offers. The one exception is
     * a Tranzzo merchant without a POS id on a Google Pay device — see that case.
     */
    const renderWalletSheet = (googleGateway: string, googleMerchantId: string) =>
        walletSheet === 'apple' ? (
            <ApplePayButton onClick={requestApplePay} text="" />
        ) : (
            <GooglePayBtn
                innerClassName={style.pp_gpay_button}
                orderAmount={orderAmount}
                currencyCode={vm?.currency_code}
                gateway={googleGateway}
                merchantId={googleMerchantId}
                isSandbox={isSandbox}
                onPaymentCompleted={onGoogleCheckoutSuccess}
                onError={(e) => onError({ title: 'Google payment error', error: e })}
            />
        );

    const renderGatewayButton = () => {
        switch (gateway) {
            case 'mypos':
                return (
                    <MyPosPayButton
                        orderId={order?.id || ''}
                        loyaltyId={getLatestLoyaltyId()}
                        orderAmount={orderAmount}
                        currencyCode={MYPOS_CURRENCY_CODE}
                        merchantName={MERCHANT_NAME}
                        onPaymentCompleted={onMyPosCheckoutSuccess}
                        onError={(e) => onError({ title: 'MyPos payment error', error: e })}
                    />
                );
            case 'tbc':
                return (
                    <TBCPayButton
                        orderId={order?.id || ''}
                        loyaltyId={getLatestLoyaltyId()}
                        onPaymentSuccess={createRedirectHandler('TBC')}
                        onPaymentFailed={createFailedHandler('TBC')}
                        onError={(e) => onError({ title: 'TBC payment error', error: e })}
                    />
                );
            case 'miapay':
                return (
                    <MiaPayButton
                        orderId={order?.id || ''}
                        onPaymentSuccess={createRedirectHandler('MiaPay')}
                        onPaymentFailed={createFailedHandler('MiaPay')}
                        onError={(e) => onError({ title: 'MiaPay payment error', error: e })}
                    />
                );
            case 'emerchant':
                return (
                    <EMerchantPay
                        orderId={order?.id || ''}
                        orderAmount={orderAmount}
                        currencyCode={MYPOS_CURRENCY_CODE}
                        isSandbox={isSandbox}
                        onPaymentCompleted={onEmerchantPaySuccess}
                        onError={(e) => onError({ title: 'EMerchantPay payment error', error: e })}
                    />
                );
            case 'portmone':
                return renderWalletSheet(GOOGLE_PAY_GATEWAY, PORTMONE_MERCHANT_ID);
            case 'tranzzo': {
                // Tranzzo mints its own Google Pay tokens: the gateway is theirs and
                // gatewayMerchantId is the POS id from merchants.extra. Without a POS
                // id the token would be undecryptable, so the Google sheet stays
                // hidden. Apple Pay needs nothing from the client — the server routes
                // merchant validation and the token by the order's provider_id
                // (payment-provider.controller.js).
                const posId = vm?.tranzzo_pos_id ?? '';
                if (walletSheet === 'google' && posId === '') return null;
                return renderWalletSheet(TRANZZO_PAY_GATEWAY, posId);
            }
            default: {
                const unreachable: never = gateway;
                return unreachable;
            }
        }
    };

    return <div className={style.wrapper}>{renderGatewayButton()}</div>;
};

const PaymentButtonsMemo = React.memo(PaymentButtons);
export { PaymentButtonsMemo as PaymentButtons };
